import React, { useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { novaThemes, NovaThemeName } from './novaTheme';
import { NovaChevronDownIcon, NovaCloseIcon } from './novaIcons';
import { NovaTaxonomyOption, optionValue } from './novaTaxonomy';

// Keeps nodes that match `term` (with their whole subtree) and the ancestors of
// any matching descendant, so search results still read as a tree.
const filterTree = (options: NovaTaxonomyOption[], term: string): NovaTaxonomyOption[] => {
  if (!term) return options;
  return options.flatMap((option) => {
    if (optionValue(option).toLowerCase().includes(term)) return [option];
    const children = filterTree(option.options ?? [], term);
    return children.length ? [{ ...option, options: children }] : [];
  });
};

const hasNestedOptions = (options: NovaTaxonomyOption[]) => options.some((option) => option.options?.length);

// Shown when a facet arrived with no options at all (e.g. the CMS taxonomy
// failed to load) — distinct from a search that simply matched nothing.
const UNAVAILABLE_LABEL = 'Options are unavailable right now. Please try again later.';

interface NovaOptionTreeProps {
  theme: NovaThemeName;
  options: NovaTaxonomyOption[];
  isSelected: (value: string) => boolean;
  onSelect: (value: string) => void;
  /** Search mode: every branch in the (already filtered) tree renders expanded. */
  expandAll?: boolean;
  emptyLabel?: string;
}

// Shared option list for NovaSingleSelect and NovaTagMultiSelect. Facets with
// sub-items (e.g. Industry → sub-sector → …) render as a collapsible tree; every
// level is selectable, mirroring Analytics Builder's FilterableDropdownTree.
const NovaOptionTree: React.FC<NovaOptionTreeProps> = ({
  theme,
  options,
  isSelected,
  onSelect,
  expandAll = false,
  emptyLabel = 'No results found',
}) => {
  const surfaces = novaThemes[theme];
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

  const toggle = (id: string) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  if (!options.length) {
    return (
      <div className="px-3 py-1.5" style={{ color: surfaces.ink.placeholder }}>
        {emptyLabel}
      </div>
    );
  }

  const renderLevel = (level: NovaTaxonomyOption[], depth: number): React.ReactNode =>
    level.map((option) => {
      const value = optionValue(option);
      const hasChildren = Boolean(option.options?.length);
      const isOpen = hasChildren && (expandAll || expanded.has(option.id));
      const selected = isSelected(value);

      return (
        <li key={option.id}>
          <div className="flex items-center" style={{ paddingLeft: Math.min(depth, 6) * 14 }}>
            {hasChildren ? (
              <button
                type="button"
                onClick={() => toggle(option.id)}
                aria-label={isOpen ? `Collapse ${option.name}` : `Expand ${option.name}`}
                aria-expanded={isOpen}
                className="ml-1 flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded"
                style={{ color: surfaces.ink.placeholder }}
              >
                <NovaChevronDownIcon
                  className={`h-3.5 w-3.5 transition-transform ${isOpen ? '' : '-rotate-90'}`}
                />
              </button>
            ) : (
              <span className="ml-1 h-6 w-6 shrink-0" />
            )}
            <button
              type="button"
              onClick={() => onSelect(value)}
              aria-pressed={selected}
              className="block min-w-0 flex-1 cursor-pointer rounded px-2 py-1.5 text-left"
              style={{ background: selected ? surfaces.surfaces.activeRow : 'transparent', color: surfaces.ink.body }}
            >
              {option.name}
            </button>
          </div>
          {isOpen && <ul>{renderLevel(option.options!, depth + 1)}</ul>}
        </li>
      );
    });

  return <ul>{renderLevel(options, 0)}</ul>;
};

// Closes an open popover on any click outside `ref`'s subtree — shared by
// NovaSingleSelect's option list and NovaTagMultiSelect's suggestion list.
const useClickOutside = (ref: React.RefObject<HTMLElement | null>, open: boolean, onClose: () => void) => {
  useEffect(() => {
    if (!open) return;
    const handleClick = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [ref, open, onClose]);
};

interface FilterShellProps {
  theme: NovaThemeName;
  label: string;
  hint?: string;
  children: React.ReactNode;
}

const FilterShell: React.FC<FilterShellProps> = ({ theme, label, hint, children }) => {
  const surfaces = novaThemes[theme];
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <div className="text-[11px] tracking-[0.08em] uppercase" style={{ color: surfaces.ink.secondary }}>
        {label}
        {hint && <span style={{ color: surfaces.ink.placeholder }}> · {hint}</span>}
      </div>
      {children}
    </div>
  );
};

interface NovaSingleSelectProps {
  theme: NovaThemeName;
  label: string;
  hint?: string;
  value: string;
  onChange: (value: string) => void;
  options: NovaTaxonomyOption[];
  placeholder?: string;
}

// Single-choice facet (e.g. Industry) — a plain button + popover list rather than
// a native <select> so it can be styled to Nova's own light/dark surfaces. Long
// or nested lists get a search box, and sub-items expand in place.
export const NovaSingleSelect: React.FC<NovaSingleSelectProps> = ({
  theme,
  label,
  hint = 'single',
  value,
  onChange,
  options,
  placeholder = 'Select…',
}) => {
  const surfaces = novaThemes[theme];
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const term = useDeferredValue(search.trim().toLowerCase());
  const rootRef = useRef<HTMLDivElement>(null);
  useClickOutside(rootRef, open, () => setOpen(false));
  const searchable = options.length > 12 || hasNestedOptions(options);
  const visibleOptions = useMemo(() => filterTree(options, term), [options, term]);

  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  return (
    <FilterShell theme={theme} label={label} hint={hint}>
      <div className="relative" ref={rootRef}>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex w-full items-center justify-between gap-2 rounded-lg border px-3 py-2 text-left text-sm"
          style={{
            background: surfaces.surfaces.field,
            borderColor: surfaces.surfaces.panelBorder,
            color: value ? surfaces.ink.heading : surfaces.ink.placeholder,
          }}
        >
          <span className="truncate">{value || placeholder}</span>
          <NovaChevronDownIcon className="h-3.5 w-3.5 shrink-0" style={{ color: surfaces.ink.placeholder }} />
        </button>
        {open && (
          <div
            className="absolute z-20 mt-1 max-h-72 w-full overflow-y-auto rounded-lg border pb-1 text-sm shadow-lg"
            style={{ background: surfaces.surfaces.card, borderColor: surfaces.surfaces.panelBorder }}
            data-lenis-prevent
          >
            {searchable && (
              <div className="sticky top-0 z-10 p-2" style={{ background: surfaces.surfaces.card }}>
                <input
                  autoFocus
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search…"
                  className="w-full rounded-md border px-2.5 py-1.5 text-sm outline-none"
                  style={{
                    background: surfaces.surfaces.field,
                    borderColor: surfaces.surfaces.panelBorder,
                    color: surfaces.ink.heading,
                  }}
                />
              </div>
            )}
            <div className={searchable ? '' : 'pt-1'}>
              <NovaOptionTree
                theme={theme}
                options={visibleOptions}
                expandAll={Boolean(term)}
                emptyLabel={options.length ? undefined : UNAVAILABLE_LABEL}
                isSelected={(optionVal) => optionVal === value}
                onSelect={(optionVal) => {
                  onChange(optionVal);
                  setOpen(false);
                }}
              />
            </div>
          </div>
        )}
      </div>
    </FilterShell>
  );
};

interface NovaTextFieldProps {
  theme: NovaThemeName;
  label: string;
  hint?: string;
  value: string;
  onChange: (value: string) => void;
  onSubmit?: () => void;
  placeholder?: string;
}

// Free-text facet (e.g. Cross Industry Report's "Trend or topic") — a single
// bordered input styled to match the other filter controls.
export const NovaTextField: React.FC<NovaTextFieldProps> = ({
  theme,
  label,
  hint = 'free text',
  value,
  onChange,
  onSubmit,
  placeholder = 'Type…',
}) => {
  const surfaces = novaThemes[theme];
  return (
    <FilterShell theme={theme} label={label} hint={hint}>
      <input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && !event.nativeEvent.isComposing && onSubmit) {
            event.preventDefault();
            onSubmit();
          }
        }}
        placeholder={placeholder}
        className="w-full rounded-lg border px-3 py-2 text-sm outline-none"
        style={{
          background: surfaces.surfaces.field,
          borderColor: surfaces.surfaces.panelBorder,
          color: surfaces.ink.heading,
        }}
      />
    </FilterShell>
  );
};

interface NovaTagMultiSelectProps {
  theme: NovaThemeName;
  label: string;
  hint?: string;
  values: string[];
  onChange: (values: string[]) => void;
  suggestions: NovaTaxonomyOption[];
  /** Whether typing a value not in `suggestions` and pressing Enter/comma adds it as-is. */
  allowFreeText?: boolean;
  placeholder?: string;
}

// Multi-choice facet (Topic, Region) — chips with a trailing text input. With
// `allowFreeText` it doubles as the "multiple, predictive" free-text field the
// Topic filter needs; without it, it's a fixed pick-list (Region). Suggestions
// with sub-items render as a tree; clicking an item toggles it in or out.
export const NovaTagMultiSelect: React.FC<NovaTagMultiSelectProps> = ({
  theme,
  label,
  hint = 'multiple',
  values,
  onChange,
  suggestions,
  allowFreeText = false,
  placeholder = 'Add…',
}) => {
  const surfaces = novaThemes[theme];
  const [inputValue, setInputValue] = useState('');
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  useClickOutside(rootRef, open, () => setOpen(false));

  const term = useDeferredValue(inputValue.trim().toLowerCase());
  const filteredSuggestions = useMemo(() => filterTree(suggestions, term), [suggestions, term]);

  const addValue = (raw: string) => {
    const next = raw.trim();
    if (!next || values.includes(next)) return;
    onChange([...values, next]);
    setInputValue('');
  };

  const removeValue = (target: string) => onChange(values.filter((v) => v !== target));

  const toggleValue = (target: string) => (values.includes(target) ? removeValue(target) : addValue(target));

  return (
    <FilterShell theme={theme} label={label} hint={hint}>
      <div className="relative" ref={rootRef}>
        <div
          className="flex min-h-[38px] w-full flex-wrap items-center gap-1.5 rounded-lg border px-2 py-1.5"
          style={{ background: surfaces.surfaces.field, borderColor: surfaces.surfaces.panelBorder }}
          onClick={() => setOpen(true)}
        >
          {values.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[13px]"
              style={{
                background: surfaces.surfaces.card,
                color: surfaces.ink.body,
                border: `1px solid ${surfaces.surfaces.panelBorder}`,
              }}
            >
              {tag}
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  removeValue(tag);
                }}
                aria-label={`Remove ${tag}`}
                className="flex h-3.5 w-3.5 items-center justify-center"
                style={{ color: surfaces.ink.placeholder }}
              >
                <NovaCloseIcon className="h-2.5 w-2.5" />
              </button>
            </span>
          ))}
          <input
            value={inputValue}
            onChange={(event) => {
              setInputValue(event.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={(event) => {
              if (allowFreeText && (event.key === 'Enter' || event.key === ',')) {
                event.preventDefault();
                addValue(inputValue);
              } else if (event.key === 'Backspace' && !inputValue && values.length) {
                removeValue(values[values.length - 1]);
              }
            }}
            placeholder={values.length ? '' : placeholder}
            className="min-w-[90px] flex-1 bg-transparent text-sm outline-none"
            style={{ color: surfaces.ink.heading }}
          />
        </div>
        {open && !suggestions.length && !allowFreeText && (
          <div
            className="absolute z-20 mt-1 w-full rounded-lg border px-3 py-2 text-sm shadow-lg"
            style={{
              background: surfaces.surfaces.card,
              borderColor: surfaces.surfaces.panelBorder,
              color: surfaces.ink.placeholder,
            }}
          >
            {UNAVAILABLE_LABEL}
          </div>
        )}
        {open && filteredSuggestions.length > 0 && (
          <div
            className="absolute z-20 mt-1 max-h-72 w-full overflow-y-auto rounded-lg border py-1 text-sm shadow-lg"
            style={{ background: surfaces.surfaces.card, borderColor: surfaces.surfaces.panelBorder }}
            data-lenis-prevent
          >
            <NovaOptionTree
              theme={theme}
              options={filteredSuggestions}
              expandAll={Boolean(term)}
              isSelected={(optionVal) => values.includes(optionVal)}
              onSelect={toggleValue}
            />
          </div>
        )}
      </div>
    </FilterShell>
  );
};
